import React from "react";
import { Box, Grid, Divider, CircularProgress } from "@mui/material";
import moment from "moment";
import { InventoryTransactionsResult } from "src/models/product/InventoryTransactionsResult";
import DataGrid from "../Desktop/components/DataGrid";
import { GridColDef } from "@mui/x-data-grid-pro";

const DATE_TIME_FORMAT = "DD/MM/YYYY HH:mm"; // Define the date-time format

const recentSalesColumns: GridColDef[] = [
  {
    field: "date",
    headerName: "Date",
    valueFormatter: (params) => (params.value ? moment(params.value).format(DATE_TIME_FORMAT) : ""),
    flex: 1,
    minWidth: 120,
  },
  { field: "quantity", headerName: "Qty", flex: 1, minWidth: 80 },
  { field: "salesPerson", headerName: "Salesperson", flex: 1, minWidth: 120 },
];

type InventoryTransactionsSummaryProps = {
  data: InventoryTransactionsResult;
  isLoading: boolean;
  error?: string;
  isMobile?: boolean;
};

const InventoryTransactionsSummary: React.FC<InventoryTransactionsSummaryProps> = ({
  data,
  isLoading,
  error,
  isMobile,
}) => {
  const noData =
    !data?.lastStockInReceivedDate &&
    !data?.lastStockInQuantity &&
    (!data?.recentSales || data?.recentSales.length === 0);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Box sx={{ color: "error.main", textAlign: "center", py: 4 }}>{error}</Box>;
  }

  if (noData) {
    return (
      <div style={{ paddingTop: "24px", paddingLeft: "16px", paddingRight: "16px" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="35" height="40" viewBox="0 0 35 40" fill="none">
            <path
              d="M4 40C2.9 40 1.95833 39.6083 1.175 38.825C0.391667 38.0417 0 37.1 0 36V4C0 2.9 0.391667 1.95833 1.175 1.175C1.95833 0.391667 2.9 0 4 0H20L32 12V20.7C31.3667 20.4667 30.7167 20.2917 30.05 20.175C29.3833 20.0583 28.7 20 28 20V14H18V4H4V36H16.7C16.9667 36.7667 17.3 37.4833 17.7 38.15C18.1 38.8167 18.5667 39.4333 19.1 40H4ZM23.8 39L21 36.2L25.2 32L21 27.8L23.8 25L28 29.2L32.2 25L35 27.8L30.85 32L35 36.2L32.2 39L28 34.85L23.8 39Z"
              fill="black"
            />
          </svg>
        </div>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: "8px", display: "flex", justifyContent: "center" }}>
          No data found for the last 90 days
        </div>
        <div
          style={{
            fontSize: 16,
            marginBottom: "24px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          There is no sales/stock-in data in last 90 days.
        </div>
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </div>
    );
  }

  return (
    <>
      <span style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Last stock-in</span>
      <Grid container mb={3} maxWidth={"300px"}>
        <Grid item xs={4}>
          <span style={{ fontSize: 14 }}>Date</span>
        </Grid>
        <Grid item xs={6}>
          <span style={{ fontSize: 14 }}>{data.lastStockInReceivedDate ?? "N/A"}</span>
        </Grid>
        <Grid item xs={4}>
          <span style={{ fontSize: 14 }}>Quantity</span>
        </Grid>
        <Grid item xs={6}>
          <span style={{ fontSize: 14 }}>{data.lastStockInQuantity || "N/A"}</span>
        </Grid>
      </Grid>
      <span style={{ fontSize: 16, fontWeight: 700 }}>Recent sales</span>
      <span style={{ fontSize: 12, color: "#707070", marginBottom: "14px", display: "block" }}>
        Listing sales data from the past 90 days only.
      </span>
      {isMobile ? (
        <Box
          sx={{
            border: "1px solid #CCCCCC",
            borderRadius: "4px",
            overflow: "scroll",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "16px" }}>
            <thead>
              <tr style={{ fontWeight: 700 }}>
                <th style={{ padding: "8px", textAlign: "left", width: "40%" }}>Date</th>
                <th style={{ padding: "8px", textAlign: "left", width: "15%" }}>Qty</th>
                <th style={{ padding: "8px", textAlign: "left", width: "45%" }}>Salesperson</th>
              </tr>
            </thead>
            <tbody>
              {data.recentSales.length === 0 ? (
                <tr style={{ borderTop: "1px solid #CCCCCC" }}>
                  <td colSpan={3} style={{ padding: "8px", textAlign: "center" }}>
                    No recent sales data available.
                  </td>
                </tr>
              ) : (
                data.recentSales.map((row, idx) => (
                  <tr key={idx} style={{ borderTop: "1px solid #CCCCCC" }}>
                    <td style={{ padding: "8px" }}>{row.date ? moment(row.date).format(DATE_TIME_FORMAT) : ""}</td>
                    <td style={{ padding: "8px" }}>{row.quantity}</td>
                    <td style={{ padding: "8px" }}>{row.salesPerson}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>
      ) : (
        <DataGrid
          rows={data.recentSales}
          columns={recentSalesColumns}
          isLoading={isLoading}
          containerWidth="100%"
          autoHeight
          containerHeight="65vh"
          columnVisibilityModel={{
            id: false,
          }}
          hideFooter
          getRowId={(row) => `${row.date}-${row.salesPerson}-${row.quantity}`}
        />
      )}
    </>
  );
};

export default InventoryTransactionsSummary;
